import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import templateService from "../../../services/template.service";
import Loading from "../../common/Loading";
import Swal from '../../helpers/swal';
import {
    DEFAULT_CALL_LANGUAGE,
    DEFAULT_CALL_VOICE,
} from './callTemplateOptions';
import {
    AdminCallTemplateFields,
    AdminCallTemplateFooter,
    AdminCallTemplateMetaGrid,
    AdminCallTemplatePage,
} from './AdminCallTemplateForm';

const EditAdminCallTemplate = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        reminderName: '',
        content: '',
        voice: DEFAULT_CALL_VOICE,
        language: DEFAULT_CALL_LANGUAGE,
        usedBy: 'CLINIC',
        mediumType: 'CALL',
    });
    const [originalData, setOriginalData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!id || id === 'undefined') {
                    throw new Error('Invalid template ID');
                }

                const response = await templateService.getEcxTemplateById(id);
                if (!response?.data) {
                    throw new Error('No template data received');
                }

                const templateData = response.data.data || response.data;

                setFormData({
                    reminderName: templateData.reminderName || '',
                    content: templateData.content || '',
                    voice: templateData.voice || DEFAULT_CALL_VOICE,
                    language: templateData.language || DEFAULT_CALL_LANGUAGE,
                    usedBy: templateData.usedBy || 'CLINIC',
                    mediumType: templateData.mediumType || 'CALL',
                });
                setOriginalData(templateData);
            } catch (err) {
                console.error('Error fetching template:', err);
                setError(err.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: err.message || 'Failed to load template. Please try again.',
                    confirmButtonColor: '#dc3545'
                }).then(() => navigate('/admin-templates'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchTemplate();
        } else {
            setLoading(false);
            setError('No template ID provided');
        }
    }, [id, navigate]);

    const hasChanges = useMemo(() => {
        if (!originalData) return false;
        return (
            formData.content !== (originalData.content || '') ||
            formData.voice !== (originalData.voice || DEFAULT_CALL_VOICE) ||
            formData.language !== (originalData.language || DEFAULT_CALL_LANGUAGE) ||
            formData.usedBy !== originalData.usedBy
        );
    }, [formData, originalData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSubmitting(true);
            await templateService.updateEcxTemplate(id, {
                reminderName: formData.reminderName,
                mediumType: 'CALL',
                content: formData.content,
                voice: formData.voice,
                language: formData.language,
                usedBy: formData.usedBy,
            });

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Call template updated successfully!',
                confirmButtonColor: '#0dcaf0',
                timer: 2000,
                showConfirmButton: true
            });
            navigate('/admin-templates');
        } catch (err) {
            console.error('Error updating Call template:', err);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    err.response?.data?.message ||
                    err.response?.data?.error ||
                    err.message ||
                    'Failed to update Call template. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (!hasChanges) {
            navigate('/admin-templates');
            return;
        }
        Swal.fire({
            title: 'Unsaved changes',
            text: 'Discard your edits and leave this page?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, discard',
            cancelButtonText: 'Keep editing'
        }).then((result) => {
            if (result.isConfirmed) {
                navigate('/admin-templates');
            }
        });
    };

    const handleReset = () => {
        if (!originalData) return;
        Swal.fire({
            title: 'Reset changes?',
            text: 'Restore all fields to the last saved values.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0dcaf0',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, reset',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData({
                    reminderName: originalData.reminderName || '',
                    content: originalData.content || '',
                    voice: originalData.voice || DEFAULT_CALL_VOICE,
                    language: originalData.language || DEFAULT_CALL_LANGUAGE,
                    usedBy: originalData.usedBy || 'CLINIC',
                    mediumType: originalData.mediumType || 'CALL',
                });
            }
        });
    };

    if (loading) {
        return (
            <AdminCallTemplatePage
                title="Edit Call Template"
                subtitle="Update master ECX call script, voice, and language"
            >
                <Loading />
            </AdminCallTemplatePage>
        );
    }

    if (error || !originalData) {
        return (
            <AdminCallTemplatePage
                title="Edit Call Template"
                subtitle="Update master ECX call script, voice, and language"
            >
                <div className="alert alert-danger admin-call-template-alert mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error || 'Template not found or failed to load.'}
                </div>
            </AdminCallTemplatePage>
        );
    }

    return (
        <AdminCallTemplatePage
            title="Edit Call Template"
            subtitle="Update master ECX call script, voice, and language"
            headerBadge={
                hasChanges ? (
                    <span className="badge bg-warning text-dark">
                        <i className="bi bi-pencil me-1"></i>
                        Unsaved changes
                    </span>
                ) : (
                    <span className="badge bg-light text-muted border">
                        Saved
                    </span>
                )
            }
        >
            <form onSubmit={handleSubmit}>
                <AdminCallTemplateMetaGrid
                    items={[
                        { label: 'Editing', value: originalData.reminderName },
                        { label: 'Medium', value: originalData.mediumType || 'CALL' },
                        { label: 'Created', value: originalData.createdAt || '—' },
                        { label: 'Modified', value: originalData.modifiedAt || '—' },
                        { label: 'Template ID', value: originalData.id, mono: true },
                        {
                            label: 'Reminder type ID',
                            value: originalData.reminderTypeId || '—',
                            mono: true,
                        },
                    ]}
                />

                <AdminCallTemplateFields
                    formData={formData}
                    onChange={handleChange}
                    contentField="content"
                    reminderNameControl={
                        <>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.reminderName}
                                readOnly
                                disabled
                            />
                            <div className="form-text">
                                Template name cannot be changed
                            </div>
                        </>
                    }
                />

                <div className="mt-4 pt-3 border-top">
                    <AdminCallTemplateFooter
                        left={
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={handleReset}
                                disabled={!hasChanges || submitting}
                            >
                                <i className="bi bi-arrow-counterclockwise me-1"></i>
                                Reset
                            </button>
                        }
                        onCancel={handleCancel}
                        onSubmitLabel="Update call template"
                        submitting={submitting}
                        submitDisabled={!hasChanges}
                        submitVariant="info"
                    />
                </div>
            </form>
        </AdminCallTemplatePage>
    );
};

export default EditAdminCallTemplate;
